import type { AvatarId, DiceSkin } from '@shared/game/types';
import type { Preferences } from '../lib/preferences';
import { AvatarPicker } from './AvatarPicker';
import { DicePicker } from './Dice';
import { Modal } from './Modal';
import { Toggle } from './Toggle';

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
  preferences: Preferences;
  onChange: (preferences: Preferences) => void;
  unlockedAvatars: AvatarId[];
  unlockedDice: DiceSkin[];
  onOpenShop: () => void;
}

export function SettingsModal({
  open,
  onClose,
  preferences,
  onChange,
  unlockedAvatars,
  unlockedDice,
  onOpenShop,
}: SettingsModalProps) {
  return (
    <Modal open={open} onClose={onClose} title="Calibración de identidad" subtitle="Estas preferencias se guardan en este dispositivo.">
      <section className="settings-section">
        <h3>Perfil</h3>
        <label className="field">
          <span>Nombre visible</span>
          <input
            value={preferences.name}
            maxLength={20}
            onChange={(event) => onChange({ ...preferences, name: event.target.value })}
            placeholder="Tu nombre de viajero"
          />
        </label>
        <AvatarPicker
          selected={preferences.avatar}
          unlocked={unlockedAvatars}
          onSelect={(avatar) => onChange({ ...preferences, avatar })}
        />
        <button type="button" className="text-link" onClick={onOpenShop}>
          Ver cosméticos y avatares del pack
        </button>
      </section>

      <section className="settings-section">
        <h3>Dado</h3>
        <p className="muted">Solo cambia el aspecto. La probabilidad es exactamente la misma en todos los diseños.</p>
        <DicePicker
          value={preferences.diceSkin}
          unlocked={unlockedDice}
          onChange={(diceSkin) => onChange({ ...preferences, diceSkin })}
        />
      </section>

      <section className="settings-section">
        <h3>Sonido y música</h3>
        <p className="muted">Todo el audio se sintetiza en tu navegador. No se descargan archivos ni se usan servicios externos.</p>
        <div className="audio-mixer">
          <Toggle
            checked={preferences.sound}
            onChange={(sound) => onChange({ ...preferences, sound })}
            label="Efectos de sonido"
            description="Dados, portales, grietas, capturas y fanfarrias."
          />
          <label className={`volume-row ${preferences.sound ? '' : 'is-disabled'}`}>
            <span>Volumen de efectos</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={preferences.sfxVolume}
              disabled={!preferences.sound}
              onChange={(event) => onChange({ ...preferences, sfxVolume: Number(event.target.value) })}
            />
            <output>{Math.round(preferences.sfxVolume * 100)}%</output>
          </label>
          <Toggle
            checked={preferences.music}
            onChange={(music) => onChange({ ...preferences, music })}
            label="Música ambiental"
            description="Un paisaje de cuerdas, campanas y viento para acompañar la partida."
          />
          <label className={`volume-row ${preferences.music ? '' : 'is-disabled'}`}>
            <span>Volumen de música</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={preferences.musicVolume}
              disabled={!preferences.music}
              onChange={(event) => onChange({ ...preferences, musicVolume: Number(event.target.value) })}
            />
            <output>{Math.round(preferences.musicVolume * 100)}%</output>
          </label>
        </div>
      </section>

      <section className="settings-section">
        <h3>Accesibilidad</h3>
        <Toggle
          checked={preferences.animations}
          onChange={(animations) => onChange({ ...preferences, animations })}
          label="Animaciones"
          description="Movimiento de fichas, dado, portal y grieta. Desactívalas si prefieres una interfaz más tranquila."
        />
      </section>
    </Modal>
  );
}
